import React, { useContext, useEffect, useRef } from "react";
import styled from "styled-components";
import { MainContext } from "../context/MainContext";
import Loader from "./Loader";

const Wrapper = styled.section`
    margin-top: 34px;
    scroll-margin-top: calc(var(--header-height) + 16px);
`;

const Heading = styled.div`
    margin-bottom: 8px;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;

    h2 {
        margin: 0;
        font-family: Georgia, "Times New Roman", serif;
        font-size: clamp(28px, 8vw, 38px);
        letter-spacing: -0.04em;
    }

    span {
        color: var(--muted);
        font-size: 13px;
    }
`;

const ResultList = styled.div`
    border-top: 1px solid var(--line-strong);
`;

const ResultRow = styled.article`
    min-height: 96px;
    padding: 16px 0;
    display: grid;
    grid-template-columns: 48px minmax(0, 1fr);
    gap: 12px 14px;
    align-items: center;
    border-bottom: 1px solid var(--line);

    @media (min-width: 620px) {
        grid-template-columns: 52px minmax(0, 1fr) auto;
        gap: 18px;
    }
`;

const Avatar = styled.div`
    width: 48px;
    height: 48px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: #8f5778;
    color: var(--text);
    font-weight: 750;

    @media (min-width: 620px) {
        width: 52px;
        height: 52px;
    }
`;

const Details = styled.div`
    min-width: 0;

    h3 {
        margin: 0;
        overflow: hidden;
        color: var(--text);
        font-size: 17px;
        line-height: 1.25;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    p {
        margin: 5px 0 0;
        color: var(--muted);
        font-size: 13px;
        text-transform: capitalize;
    }

    .verified {
        margin-left: 8px;
        color: var(--accent-strong);
        font-size: 12px;
        font-weight: 700;
    }
`;

const Contact = styled.a`
    min-height: 44px;
    padding: 10px 14px;
    grid-column: 1 / -1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--line-strong);
    border-radius: var(--radius-sm);
    background: var(--surface);
    color: var(--text);
    font-size: 14px;
    font-weight: 700;

    &:hover {
        border-color: var(--accent);
        color: var(--accent-strong);
    }

    @media (min-width: 620px) {
        grid-column: auto;
    }
`;

const Empty = styled.div`
    margin-top: 18px;
    padding: 28px 20px;
    border: 1px solid var(--line);
    border-radius: var(--radius-md);
    color: var(--text-soft);
    text-align: center;

    h2 { margin: 0 0 6px; color: var(--text); font-size: 20px; }
    p { margin: 0; color: var(--muted); font-size: 14px; }
`;

const initials = (name) => (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const Results = () => {
    const { profilesContext, isSearchingContext } = useContext(MainContext);
    const [profiles] = profilesContext;
    const [isSearching] = isSearchingContext;
    const resultsRef = useRef(null);

    useEffect(() => {
        if (!isSearching && resultsRef.current) {
            resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [isSearching]);

    if (isSearching) return <Loader/>;

    return (
        <Wrapper ref={ resultsRef } aria-live="polite">
            { profiles.length === 0 ? (
                <Empty>
                    <h2>No matches yet</h2>
                    <p>Try a wider age range or search again later.</p>
                </Empty>
            ) : (
                <>
                    <Heading>
                        <h2>People to meet</h2>
                        <span>{ profiles.length } { profiles.length === 1 ? "result" : "results" }</span>
                    </Heading>
                    <ResultList>
                        { profiles.map((profile) => (
                            <ResultRow key={ profile._id }>
                                <Avatar aria-hidden="true">{ initials(profile.displayName) }</Avatar>
                                <Details>
                                    <h3>
                                        { profile.displayName || "No name" }, { profile.age }
                                        { profile.po && <span className="verified">Verified</span> }
                                    </h3>
                                    <p>{ profile.gender }</p>
                                </Details>
                                <Contact href={ `https://t.me/${ profile.username }` } target="_blank" rel="noreferrer">
                                    Contact on Telegram
                                </Contact>
                            </ResultRow>
                        )) }
                    </ResultList>
                </>
            ) }
        </Wrapper>
    );
};

export default Results;
